import { useId, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Text } from '@/components/ui/Text'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { Spinner } from '@/components/ui/Spinner'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog'
import { Label } from '@/components/ui/Label'
import { fetchGql } from '@/lib/graphql_client'
import { CreateOrGetNaturalPersonByEmailDocument, type NaturalPersonIdentityStatus } from '@/gql/graphql'
import { getIdentityErrorMessage } from '@/lib/identity/identity-errors'
import { IdentityBlockedBanner, type IdentityBlockedStatus } from '@/components/shared/IdentityBlockedBanner'
import { IDENTITY_STATUS_LABEL } from '@/lib/identity/identity-status'

function asBlockedStatus(status: NaturalPersonIdentityStatus | undefined): IdentityBlockedStatus | null {
  if (!status || !IDENTITY_STATUS_LABEL[status]?.blocked) return null
  return status as IdentityBlockedStatus
}

export interface CreatedPerson {
  party_id: string
  name: string
  email?: string | null
}

export interface CreatePersonDialogProps {
  open: boolean
  onClose: () => void
  /** Called with the created (or found, by email) person once the mutation succeeds. */
  onCreated: (person: CreatedPerson) => void
  /** Pre-fills the name field, e.g. with the picker's current search text. */
  initialName?: string
  actingTenantId?: string
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * "Add new person" dialog — name + email → `CreateOrGetNaturalPersonByEmail` (server-side dedup
 * by email). Shared by the top-level `partyPicker` field and the `listBuilder` partyPicker
 * sub-field (entity-incorporation-flow T04), so both create people the same way.
 *
 * On success it seeds the naturalPersons picker cache (so the Combobox resolves the new label
 * immediately) and the dynamicOptions NaturalPersons cache (for `useAppointeeNameLookup` / KYC
 * step prefill), then hands the person to `onCreated`. Both caches are written by party id, not
 * appended: the mutation returns an EXISTING person when the email matches, who may already be
 * listed.
 *
 * Mount it with `key` changing per open (or only while open) so the form resets between uses.
 */
export function CreatePersonDialog({
  open,
  onClose,
  onCreated,
  initialName = '',
  actingTenantId,
}: CreatePersonDialogProps) {
  const idBase = useId()
  const nameId = `${idBase}-name`
  const emailId = `${idBase}-email`

  const [nameValue, setNameValue] = useState(initialName)
  const [emailValue, setEmailValue] = useState('')
  const [nameError, setNameError] = useState<string | undefined>()
  const [emailError, setEmailError] = useState<string | undefined>()
  const [serverError, setServerError] = useState<string | null>(null)
  const [blocked, setBlocked] = useState<{ person: CreatedPerson; status: IdentityBlockedStatus } | null>(null)

  const queryClient = useQueryClient()

  const createPersonMutation = useMutation({
    mutationFn: ({ name, email }: { name: string; email: string }) =>
      fetchGql(CreateOrGetNaturalPersonByEmailDocument, {
        name,
        email,
        ...(actingTenantId ? { actingTenantId } : {}),
      }),
    onSuccess: (data) => {
      const person = data.createOrGetNaturalPersonByEmail

      queryClient.setQueryData(
        ['naturalPersons', 'picker', actingTenantId],
        (old: { naturalPersons: Array<{ party_id: string; name: string }> } | undefined) => ({
          naturalPersons: [
            ...(old?.naturalPersons ?? []).filter((p) => p.party_id !== person.party_id),
            { party_id: person.party_id, name: person.name },
          ],
        }),
      )

      // The partial key ['dynamicOptions','NaturalPersons'] matches this entry via a
      // getQueriesData prefix scan in useAppointeeNameLookup.
      queryClient.setQueryData(
        ['dynamicOptions', 'NaturalPersons', { actingTenantId }],
        (old: Array<{ value: string; label: string }> | undefined) => [
          ...(old ?? []).filter((o) => o.value !== person.party_id),
          { value: person.party_id, label: person.name },
        ],
      )

      const createdPerson = { party_id: person.party_id, name: person.name, email: person.email }
      const blockedStatus = asBlockedStatus(person.identityStatus)
      // codex-qa C3 (MR !504, FR-F11): a reuse-by-email can come back blocked (e.g. CONFLICT_PENDING)
      // — surface that instead of silently handing the person to the caller.
      if (blockedStatus) {
        setBlocked({ person: createdPerson, status: blockedStatus })
        return
      }
      onCreated(createdPerson)
      onClose()
    },
    onError: (err) => {
      setServerError(getIdentityErrorMessage(err) ?? 'Failed to create person. Please try again.')
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    e.stopPropagation()
    let valid = true

    if (!nameValue.trim()) {
      setNameError('Full name is required')
      valid = false
    } else {
      setNameError(undefined)
    }

    if (!emailValue.trim()) {
      setEmailError('Email address is required')
      valid = false
    } else if (!EMAIL_PATTERN.test(emailValue.trim())) {
      setEmailError('Enter a valid email address')
      valid = false
    } else {
      setEmailError(undefined)
    }

    if (!valid) return
    setServerError(null)
    createPersonMutation.mutate({ name: nameValue.trim(), email: emailValue.trim() })
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose() }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle asChild>
            <Text variant="headline-sm" as="h2">Add new person</Text>
          </DialogTitle>
          <DialogDescription asChild>
            <Text variant="body-sm" color="muted">
              Creates or finds an existing person by email address.
            </Text>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} noValidate>
          <div className="space-y-4 py-4">
            <div className="space-y-1.5">
              <Label htmlFor={nameId}>
                <Text variant="label-sm">Full Name</Text>
              </Label>
              <Input
                id={nameId}
                value={nameValue}
                onChange={(e) => { setNameValue(e.target.value); setBlocked(null) }}
                placeholder="Jane Smith"
                aria-invalid={!!nameError || undefined}
                aria-describedby={nameError ? `${nameId}-error` : undefined}
                autoComplete="name"
              />
              {nameError && (
                <Text id={`${nameId}-error`} variant="label-sm" color="danger">
                  {nameError}
                </Text>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor={emailId}>
                <Text variant="label-sm">Email</Text>
              </Label>
              <Input
                id={emailId}
                type="email"
                value={emailValue}
                onChange={(e) => { setEmailValue(e.target.value); setBlocked(null) }}
                placeholder="jane@example.com"
                aria-invalid={!!emailError || undefined}
                aria-describedby={emailError ? `${emailId}-error` : undefined}
                autoComplete="email"
              />
              {emailError && (
                <Text id={`${emailId}-error`} variant="label-sm" color="danger">
                  {emailError}
                </Text>
              )}
            </div>

            {serverError && (
              <div role="alert" className="rounded-md border border-danger-subtle bg-danger-subtle/40 px-3 py-2">
                <Text variant="body-sm" color="danger">{serverError}</Text>
              </div>
            )}

            {blocked && <IdentityBlockedBanner status={blocked.status} />}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={createPersonMutation.isPending}
            >
              Cancel
            </Button>
            {blocked ? (
              <Button type="button" onClick={() => { onCreated(blocked.person); onClose() }}>
                Use this person anyway
              </Button>
            ) : (
              <Button type="submit" disabled={createPersonMutation.isPending}>
                {createPersonMutation.isPending && <Spinner className="mr-2 h-4 w-4" />}
                Create
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
